// ─────────────────────────────────────────────────────────────────────
// ENTRA CONFIGURATION — the browser half of internal staff sign-in.
//
// This is CONFIGURATION, not secrets. A SPA client id and an authority are
// public by construction: they are in the bundle, in the redirect URL, and in
// every network trace. Nothing here is a credential, and nothing here grants
// anything — see docs/architecture/SECURITY.md §4e.
//
// The rule this module exists to enforce is the same one the API applies to
// its own `ENTRA_*` pair (apps/api/src/env.ts): configuration is ALL OR
// NOTHING. Partial configuration is always a mistake, and a half-configured
// sign-in that quietly turns itself off is the worst outcome — it looks
// deliberate. So: nothing set is a disabled feature, everything set is an
// enabled one, and anything in between is reported as broken.
// ─────────────────────────────────────────────────────────────────────

export interface EntraBrowserConfig {
  /** The SPA app registration's Application (client) id. */
  clientId: string;
  /** Directory (tenant) id. Becomes the tenant-pinned authority. */
  tenantId: string;
  /**
   * The scope the browser asks for. This must be the API's exposed scope
   * (`api://<api-client-id>/<scope>`), NOT a Microsoft Graph scope and NOT
   * `openid`/`profile` — see `assertApiScope` below for why that matters.
   */
  apiScope: string;
  /** Where Entra returns to. Defaults to the page's own origin. */
  redirectUri: string;
}

export type EntraConfigStatus =
  /** Every required value present — the sign-in flow is available. */
  | { state: 'configured'; config: EntraBrowserConfig }
  /** Nothing configured. The flow is off, deliberately and visibly. */
  | { state: 'disabled' }
  /** Some but not all. Named so a deploy is fixed once, not variable by variable. */
  | { state: 'incomplete'; missing: string[]; problems: string[] };

/** The required variables, in the order a reader would set them. */
export const ENTRA_REQUIRED_VARS = [
  'VITE_ENTRA_CLIENT_ID',
  'VITE_ENTRA_TENANT_ID',
  'VITE_ENTRA_API_SCOPE',
] as const;

/** Optional: only needed when the redirect URI is not the page's own origin. */
export const ENTRA_OPTIONAL_VARS = ['VITE_ENTRA_REDIRECT_URI'] as const;

/**
 * Multi-tenant authority aliases. Accepting one of these would mean accepting
 * a sign-in from ANY Microsoft directory — a personal outlook.com account
 * included — and Starpoint's Entra sign-in is for its own staff. The API pins
 * the tenant on its side too (issuer + `tid`), so a token from elsewhere would
 * be rejected anyway; refusing it here turns a confusing 401 into a clear
 * configuration error.
 */
const MULTI_TENANT_ALIASES = ['common', 'organizations', 'consumers'];

export type EnvLike = Record<string, string | undefined>;

const clean = (v: string | undefined) => (typeof v === 'string' ? v.trim() : '');

/** The inert page Entra returns the popup to. See apps/web/public/. */
export const DEFAULT_REDIRECT_PATH = '/auth-callback.html';

/**
 * Where Entra sends the sign-in popup back to — ON THE ORIGIN THE PAGE IS
 * ACTUALLY ON.
 *
 * A POPUP HANDSHAKE IS SAME-ORIGIN OR IT IS NOTHING. MSAL runs in the opener
 * and reads the response by polling the popup's URL. A cross-origin read
 * throws, so it never sees the response and never stops polling: the button
 * says «Logger inn…» for ever, with no error anywhere, because nothing
 * failed — it is still waiting.
 *
 * This app is served on THREE hostnames (dev.spoint.no, star-point.no and
 * starpointdev.azurewebsites.net). A redirect URI pinned at build time is
 * therefore correct on exactly one of them and silently broken on the other
 * two, however many URIs are registered in Entra — registration decides what
 * Entra will ACCEPT, and this decides what the client SENDS.
 *
 * So the default is derived from the live origin, and a configured PATH is
 * resolved against it. An absolute URL is still honoured as an escape hatch,
 * but it pins the origin and brings the same trap back.
 */
export function resolveRedirectUri(configured: string, origin: string): string {
  if (/^https?:\/\//i.test(configured)) return configured;
  if (!origin) return '';
  const path = configured || DEFAULT_REDIRECT_PATH;
  return `${origin.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
}

/** The tenant-pinned authority URL. Never `/common`. */
export function entraAuthority(tenantId: string): string {
  return `https://login.microsoftonline.com/${tenantId}`;
}

/**
 * Read and validate the browser's Entra configuration.
 *
 * `origin` is passed in rather than read from `window` so the defaulting rule
 * is testable and so this module stays usable outside a browser.
 */
export function readEntraConfig(env: EnvLike, origin = ''): EntraConfigStatus {
  const clientId = clean(env.VITE_ENTRA_CLIENT_ID);
  const tenantId = clean(env.VITE_ENTRA_TENANT_ID);
  const apiScope = clean(env.VITE_ENTRA_API_SCOPE);
  // NOT `… || origin`, which was the app root — the SPA, whose hash router
  // ate Entra's response before MSAL could read it (#380). The default is the
  // inert callback page on the page's own origin.
  const redirectUri = resolveRedirectUri(clean(env.VITE_ENTRA_REDIRECT_URI), origin);

  const present = [clientId, tenantId, apiScope].filter(Boolean).length;
  if (present === 0) return { state: 'disabled' };

  const missing = ENTRA_REQUIRED_VARS.filter((name) => !clean(env[name]));
  const problems: string[] = [];

  if (tenantId && MULTI_TENANT_ALIASES.includes(tenantId.toLowerCase())) {
    problems.push(
      `VITE_ENTRA_TENANT_ID is "${tenantId}", which accepts sign-ins from any Microsoft ` +
        'directory. Set the Starpoint directory (tenant) id instead.',
    );
  }
  if (apiScope && !apiScope.includes('/')) {
    problems.push(
      `VITE_ENTRA_API_SCOPE is "${apiScope}", which is not an API scope. It must be the ` +
        'scope the API app registration exposes, e.g. api://<api-client-id>/Api.Access — ' +
        'asking only for openid/profile yields an ID token the API will not accept.',
    );
  }
  if (!redirectUri) {
    problems.push(
      'No redirect URI: the page has no origin to derive one from, and ' +
        'VITE_ENTRA_REDIRECT_URI is unset or is not an absolute URL.',
    );
  }

  if (missing.length > 0 || problems.length > 0) {
    return { state: 'incomplete', missing: [...missing], problems };
  }
  return { state: 'configured', config: { clientId, tenantId, apiScope, redirectUri } };
}
